import React, { FC } from 'react';
import { graphql, PageProps } from 'gatsby';
import { MainLayout } from '../MainLayout';
import 'basiclightbox/dist/basicLightbox.min.css';
import { IAlternativesQuery } from '../../queries/alternatives/types';
import { ICaseFrontmatter, ICaseQueryEdge } from '../../queries/cases/types';
import { CasePageContent } from '../../components/case/CasePageContent';
import { TempoCasePageContent } from '../../components/case/TempoCasePage';
import { ArchiveCase } from '../../components/case/ArchiveCase';

export type IData = {
  page: {
    frontmatter: ICaseFrontmatter;
    html: string;
  };
  projects: {
    edges: ICaseQueryEdge[];
  };
} & IAlternativesQuery;

type IProps = PageProps<IData, { slug: string }>;

const pages: Record<string, FC<any>> = {
  active: CasePageContent,
  archived: ArchiveCase,
  inDevelop: TempoCasePageContent,
};

export default ({ data, pageContext: { slug } }: IProps) => {
  const page = data?.page;
  const projects = data.projects.edges.filter((edge) => edge.node);
  const PageContent = pages[page.frontmatter.status];

  return (
    <MainLayout
      locale="en"
      title={page.frontmatter.seo?.title}
      description={page.frontmatter.seo?.description}
      keywords={page.frontmatter.seo?.keywords}
    >
      {PageContent && (
        <PageContent slug={slug} page={page} projects={projects} />
      )}
    </MainLayout>
  );
};

export const query = graphql`
  query ($slug: String!) {
    page: markdownRemark(fileAbsolutePath: { eq: $slug }) {
      html
      fileAbsolutePath
      frontmatter {
        title
        status
        description
        color
        link
        seo {
          title
          description
          keywords
        }
        preview {
          backgroundImage
          subtitle
          description
          descriptionParagraphs
          deviceImage
          termsBackground
          terms {
            image
            title
            text
          }
          afterTermsDescription
          webMobile
        }
        taskAndSolution {
          solutionTitleAddon
          solutionParagraphs
          solutionImg
          taskTitle
          taskTitleAddon
          taskParagraphs
          taskImg
        }
        process {
          title
          backgroundColor
          descriptionParagraphs
          descriptionAddonParagraphs
          mostDifficultParagraphs
          solutionsParagraphs
          conclusionsParagraphs
          adviceText
          linesColor
          projectManagerWords {
            img
            name
            position
            text
          }
        }
        cards
        stack {
          color
          infoText
          title
          descriptionParagraphs
          descriptionText
          styles {
            columnsCount
          }
          mobileList {
            iconName
            text
            caption
          }
          backendList {
            iconName
            text
          }
          customList {
            caption
            items {
              iconName
              text
            }
          }
          androidList {
            iconName
            text
          }
          iosList {
            iconName
            text
          }
        }
        feedback {
          name
          position
          text
        }
        result {
          rotate
          platforms
          hidePlatforms
          linkStore {
            iOS
            Android
          }
          screens
          title
          resultParagraphs
          colors {
            primary
            secondary
          }
          isDarkPrimaryColor
        }
        developmentPlans {
          descriptionParagraphs
        }
        presentation
        offer {
          backgroundColor
          background
          ideaImg
          buttonColor
          linearGradient
        }
        similarCases
      }
    }

    projects: allMarkdownRemark(
      filter: { fileAbsolutePath: { regex: "/cases/projects/" } }
      sort: { fields: fileAbsolutePath, order: ASC }
    ) {
      ...CaseFields
    }
  }
`;
